import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

import { FaPlus, FaTrash } from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiSwitch from "components/VuiSwitch";
import { DialogTitle } from "@mui/material";
import VuiAutocomplete from "components/AutoComplete";
import { border } from "@mui/system";
import { Formik } from "formik";
import { IntegrationSchema } from "validations";
import useCommon from "hooks/useCommon";
import DynamicFieldBuilder from "../utils/DynamicFeildBuilder";

const EMPTY_CREDENTIAL_FIELD = {
  name: "",
  label: "",
  type: "string",
  required: false,
  secret: false,
  placeholder: "",
  helpText: "",
  options: [],
};

const FIELD_TYPE_OPTIONS = [
  { value: "string", label: "String" },
  { value: "password", label: "Password" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "json", label: "JSON" },
];

const CreateIntegrationDialog = ({ open, onClose, onSubmit }) => {
  const { STORE } = useCommon();
  const { createLoading, createError } = STORE((state) => state.integrations);

  const [webhookEventInput, setWebhookEventInput] = useState("");

  const handleCreateIntegration = (values, formikHelpers) => {
    onSubmit(values);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <VuiTypography variant="button" fontWeight="bold">
          Add New Integration
        </VuiTypography>
      </DialogTitle>
      <Formik
        initialValues={{
          key: "",
          displayName: "",
          description: "",
          logoUrl: "",
          learnMore: "",
          status: "enabled", // enabled when created
          supportsWebhooks: true,
          supportsPolling: false,
          supportsPropertyDiscovery: false,
          credentialFields: [],
          mappingFields: [],
          defaultWebhookEvents: [],
        }}
        validationSchema={IntegrationSchema}
        onSubmit={handleCreateIntegration}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
          const handleAddWebhookEvent = () => {
            const value = webhookEventInput.trim();

            if (!value) return;
            if (values.defaultWebhookEvents.includes(value)) return;

            setFieldValue("defaultWebhookEvents", [...values.defaultWebhookEvents, value]);

            setWebhookEventInput("");
          };

          const handleRemoveWebhookEvent = (event) => {
            setFieldValue(
              "defaultWebhookEvents",
              values.defaultWebhookEvents.filter((e) => e !== event)
            );
          };

          const addCredentialField = () => {
            setFieldValue("credentialFields", [
              ...values.credentialFields,
              { ...EMPTY_CREDENTIAL_FIELD },
            ]);
          };

          const updateCredentialField = (index, field, value) => {
            setFieldValue(`credentialFields.${index}.${field}`, value);
          };

          const removeCredentialField = (index) => {
            const updated = [...values.credentialFields];
            updated.splice(index, 1);
            setFieldValue("credentialFields", updated);
          };
          return (
            <>
              <DialogContent>
                {/* ───────────────── BASIC INFO ───────────────── */}
                <Grid container columnSpacing={2} rowSpacing={0}>
                  <Grid item xs={12} md={6}>
                    <VuiBox>
                      <VuiTypography variant="caption">Provider Key *</VuiTypography>
                      <VuiInput
                        gradient
                        fullWidth
                        placeholder="e.g. mews"
                        name="key"
                        value={values.key}
                        onChange={handleChange}
                        // value={form.key}
                        // onChange={(e) => setForm({ ...form, key: e.target.value })}
                      />
                      {touched.key && errors.key && (
                        <VuiTypography variant="caption" color="error">
                          {errors.key}
                        </VuiTypography>
                      )}
                    </VuiBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <VuiBox>
                      <VuiTypography variant="caption">Display Name *</VuiTypography>
                      <VuiInput
                        gradient
                        fullWidth
                        placeholder="MEWS"
                        // value={form.displayName}
                        // onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                        name="displayName"
                        value={values.displayName}
                        onChange={handleChange}
                      />
                      {touched.displayName && errors.displayName && (
                        <VuiTypography variant="caption" color="error">
                          {errors.displayName}
                        </VuiTypography>
                      )}
                    </VuiBox>
                  </Grid>

                  <Grid item xs={12}>
                    <VuiBox>
                      <VuiTypography variant="caption">Description</VuiTypography>
                      <VuiInput
                        gradient
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Short description of this integration"
                        // value={form.description}
                        // onChange={(e) => setForm({ ...form, description: e.target.value })}
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                      />
                    </VuiBox>
                  </Grid>

                  <Grid item xs={12}>
                    <VuiBox>
                      <VuiTypography variant="caption">Logo URL</VuiTypography>
                      <VuiInput
                        gradient
                        fullWidth
                        placeholder="https://..."
                        // value={form.logoUrl}
                        // onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                        name="logoUrl"
                        value={values.logoUrl}
                        onChange={handleChange}
                        inputProps={{ maxLength: 800 }}
                      />
                      {touched.logoUrl && errors.logoUrl && (
                        <VuiTypography variant="caption" color="error">
                          {errors.logoUrl}
                        </VuiTypography>
                      )}
                    </VuiBox>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <VuiBox>
                    <VuiTypography variant="caption">Learn More URL</VuiTypography>

                    <VuiInput
                      gradient
                      fullWidth
                      placeholder="https://docs.mews.com/..."
                      name="learnMore"
                      value={values.learnMore}
                      onChange={handleChange}
                      inputProps={{ maxLength: 800 }}
                    />

                    <VuiTypography variant="caption" opacity={0.6} mt={0.5} display="block">
                      Optional documentation or provider guide URL.
                    </VuiTypography>

                    {touched.learnMore && errors.learnMore && (
                      <VuiTypography variant="caption" color="error">
                        {errors.learnMore}
                      </VuiTypography>
                    )}
                  </VuiBox>
                </Grid>

                {/* ───────────────── CREDENTIAL FIELDS ───────────────── */}
                <DynamicFieldBuilder
                  title="Credential Fields"
                  description="Define credentials required when a hotel configures this integration."
                  fields={values.credentialFields}
                  fieldKey="credentialFields"
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                />
                {/* ───────────────── MAPPING FIELDS ───────────────── */}
                <DynamicFieldBuilder
                  title="Mapping Fields"
                  description="Define fields used to map external provider data."
                  fields={values.mappingFields}
                  fieldKey="mappingFields"
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                />
                {/* ───────────────── WEBHOOK EVENTS ───────────────── */}
                {values.supportsWebhooks && (
                  <VuiBox mt={3}>
                    <VuiTypography variant="button">Default Webhook Events</VuiTypography>

                    <VuiTypography variant="caption" opacity={0.7} mb={1}>
                      <br />
                      Format: <strong>resource.action</strong>
                    </VuiTypography>

                    <VuiBox display="flex" gap={1}>
                      <VuiInput
                        gradient
                        fullWidth
                        placeholder="e.g. reservation.created"
                        value={webhookEventInput}
                        onChange={(e) => setWebhookEventInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddWebhookEvent();
                          }
                        }}
                      />

                      <VuiButton
                        color="primary"
                        variant="contained"
                        onClick={handleAddWebhookEvent}
                      >
                        Add
                      </VuiButton>
                    </VuiBox>

                    {/* Chips */}
                    <VuiBox display="flex" flexWrap="wrap" gap={1} mt={2}>
                      {values.defaultWebhookEvents.map((event) => (
                        <VuiBox
                          key={event}
                          px={1.5}
                          py={0.75}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{
                            borderRadius: "8px",
                            background:
                              "linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06))",
                            border: "1px solid rgba(255,255,255,0.15)",
                          }}
                        >
                          <VuiTypography variant="caption">{event}</VuiTypography>

                          <VuiButton
                            variant="text"
                            color="error"
                            onClick={() => handleRemoveWebhookEvent(event)}
                            sx={{ minWidth: 18, padding: 0 }}
                          >
                            ✕
                          </VuiButton>
                        </VuiBox>
                      ))}
                    </VuiBox>
                  </VuiBox>
                )}

                {/* ───────────────── CAPABILITIES ───────────────── */}
                <VuiBox mt={1}>
                  <VuiTypography variant="button">Capabilities</VuiTypography>

                  <VuiBox display="flex" gap={2} width="100%" mt={2}>
                    <VuiBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      flex={1}
                    >
                      <VuiSwitch
                        color="success"
                        checked={values.supportsWebhooks}
                        onChange={() => setFieldValue("supportsWebhooks", !values.supportsWebhooks)}
                      />
                      <VuiTypography variant="caption">Webhooks</VuiTypography>
                    </VuiBox>

                    <VuiBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      flex={1}
                    >
                      <VuiSwitch
                        color="success"
                        checked={values.supportsPolling}
                        onChange={() => setFieldValue("supportsPolling", !values.supportsPolling)}
                      />
                      <VuiTypography variant="caption">Polling</VuiTypography>
                    </VuiBox>

                    <VuiBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      flex={1}
                    >
                      <VuiSwitch
                        color="success"
                        checked={values.supportsPropertyDiscovery}
                        onChange={() =>
                          setFieldValue(
                            "supportsPropertyDiscovery",
                            !values.supportsPropertyDiscovery
                          )
                        }
                      />
                      <VuiTypography variant="caption">Property Discovery</VuiTypography>
                    </VuiBox>
                  </VuiBox>
                </VuiBox>
              </DialogContent>

              <DialogActions>
                <VuiButton variant="outlined" color="primary" onClick={onClose}>
                  Cancel
                </VuiButton>

                <VuiButton color="primary" onClick={handleSubmit} disabled={createLoading}>
                  {createLoading ? "Creating..." : "Create Integration"}
                </VuiButton>
              </DialogActions>
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CreateIntegrationDialog;
